import { useCallback, useEffect } from 'react';
import { useRequestAnimationFrame } from './useRequestAnimationFrame';

export interface ListenerOptions extends AddEventListenerOptions {
  throttle?: boolean;
}

export const useWindowListener = <K extends keyof WindowEventMap>(
  key: K,
  fn: (arg: WindowEventMap[K]) => void,
  { throttle = true, passive, once }: ListenerOptions = {},
) => {
  const throttled = useRequestAnimationFrame(fn);

  const actual = throttle ? throttled : fn;

  const unlessDefaultPrevented = useCallback(
    (event: WindowEventMap[K]) => {
      if (event.defaultPrevented) {
        return;
      }
      actual(event);
    },
    [actual],
  );

  useEffect(() => {
    window.addEventListener(key, unlessDefaultPrevented, { passive, once });

    return () => {
      window.removeEventListener(key, unlessDefaultPrevented);
    };
  }, [unlessDefaultPrevented, key, passive, once]);
};
